import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';
import type { TableInfiniteDataSource } from './createTableInfiniteDataSource';
import type { AnyColumnDef } from './Any';
import z from 'zod';
import { type TableState, tableStateSchema } from './TableState';
import { getDefaultValues } from './getDefaultValues';

type TableDataProviderProps = {
  dataSource: TableInfiniteDataSource;
  columns: AnyColumnDef[];
  tableStateSchema: z.ZodSchema;
  defaultValues: TableState;
};

const TableDataContext = createContext<TableDataProviderProps | null>(null);

export const TableDataProvider = ({
  children,
  dataSource,
  columns,
}: PropsWithChildren<Omit<TableDataProviderProps, 'tableStateSchema' | 'defaultValues'>>) => {
  const adjustedSchema = useMemo(
    () =>
      tableStateSchema.extend({
        filters: dataSource.filtersSchema ? dataSource.filtersSchema.default({}) : z.object({}).default({}),
      }),
    [dataSource.filtersSchema],
  );

  const defaultValues = useMemo(() => getDefaultValues(columns, adjustedSchema), [columns, adjustedSchema]);

  return (
    <TableDataContext.Provider value={{ dataSource, columns, tableStateSchema: adjustedSchema, defaultValues }}>
      {children}
    </TableDataContext.Provider>
  );
};

export const useTableData = () => {
  const context = useContext(TableDataContext);
  if (!context) {
    throw new Error('TableContextProvider not found');
  }
  return context;
};

export const useTableName = () => {
  const context = useTableData();
  return context.dataSource.queryKey.join('-');
};
